/**
 * AuthService — servis za eID autentikaciju (elektronska lična karta).
 * Koristi oidc-client biblioteku za OAuth2/OpenID Connect integraciju sa državnim eID sistemom.
 * Singleton instanca se eksportuje kao default.
 */

import { Log, UserManager } from 'oidc-client';
import {
  STS_AUTHORITY,
  CLIENT_ID,
  CLIENT_SCOPE,
  CLIENT_ROOT,
} from './oidcConfig';

/**
 * Servis za login/logout, silent token renewal i upravljanje korisničkom OIDC sesijom.
 */
class AuthService {
  constructor() {
    const settings = {
      authority: STS_AUTHORITY,
      client_id: CLIENT_ID,
      redirect_uri: `${CLIENT_ROOT}signin-oidc`,
      silent_redirect_uri: `${CLIENT_ROOT}silent-renew.html`,
      post_logout_redirect_uri: `${CLIENT_ROOT}signout-callback-oidc`,
      response_type: 'code',
      scope: CLIENT_SCOPE,
    };

    this.userManager = new UserManager(settings);
    // Singleton in-flight renewal — sprečava paralelne pozive token endpointa
    // (refresh token rotation: drugi poziv sa istim tokenom dobija invalid_grant)
    this._renewPromise = null;

    Log.logger = {
      debug: () => {},
      info: () => {},
      warn: () => {},
      error: () => {},
    };
    Log.level = Log.NONE;
  }

  /**
   * Vraća trenutnog korisnika iz session storage-a.
   * @returns {Promise<import('oidc-client').User|null>} OIDC User objekt ili `null` ako nije prijavljen.
   */
  getUser() {
    return this.userManager.getUser();
  }

  /**
   * Pokreće login proces — redirect na eID login stranicu.
   * @returns {Promise<void>}
   */
  login() {
    return this.userManager.signinRedirect();
  }

  /**
   * Obnavlja JWT token bez redirecta (silent renewal preko skrivenog iframe-a).
   * Singleton pattern — bez obzira koliko pozivalaca pozove renewToken() istovremeno,
   * samo jedan signinSilent() poziv ide ka eID token endpointu. Svi deljeni pozivači
   * dobijaju isti Promise i isti rezultat, čime se eliminiše invalid_grant pri
   * refresh token rotaciji.
   * @returns {Promise<import('oidc-client').User>} Obnovljeni OIDC User sa novim access_token-om.
   */
  renewToken() {
    if (!this._renewPromise) {
      this._renewPromise = this.userManager.signinSilent().finally(() => {
        this._renewPromise = null;
      });
    }
    return this._renewPromise;
  }

  /**
   * Uklanja korisnika iz session storage-a.
   * Koristi se za čišćenje neispravne sesije.
   * @returns {Promise<void>}
   */
  removeUser() {
    return this.userManager.removeUser();
  }

  /**
   * Logout — odjavljuje korisnika i redirect na eID logout.
   * @returns {Promise<void>}
   */
  logout() {
    return Promise.resolve(this.userManager.signoutRedirect());
  }

  /**
   * Callback nakon uspešnog login redirect-a — parsira JWT token iz URL-a.
   * "No matching state" greška (React Strict Mode dupli mount) se tiho ignoriše i vraća `null`.
   * @returns {Promise<import('oidc-client').User|null>} Parsirani User ili `null` pri poznatom Strict Mode race-u.
   */
  getToken() {
    return this.userManager
      .signinRedirectCallback()
      .catch(function (e) {
        if (e && e.message && e.message.includes('No matching state')) {
          return null;
        }
        throw e;
      });
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;
